import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Paper,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { deleteInvoice } from "../../../../services/Admin/InvoicesApi";

const DeleteInvoiceDialog = ({ open, onClose, invoice, onDeleted }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleDelete = async () => {
    if (!invoice || !invoice.id) {
      setError("لا يوجد فاتورة محددة للحذف");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const response = await deleteInvoice(invoice.id);

      setSuccess(true);

      // إغلاق الديالوج بعد 1.5 ثانية
      setTimeout(() => {
        onDeleted && onDeleted(invoice.id);
        onClose();
      }, 1500);
    } catch (err) {
      console.error("خطأ في حذف الفاتورة:", err);
      setError(err.message || "حدث خطأ في حذف الفاتورة");
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError("");
      setSuccess(false);
      onClose();
    }
  };

  if (!invoice) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth dir="rtl">
      <DialogTitle
        sx={{
          fontWeight: "bold",
          textAlign: "right",
          display: "flex",
          alignItems: "center",
          gap: 1,
          backgroundColor: "error.light",
          color: "error.dark",
        }}
      >
        <DeleteIcon />
        تأكيد حذف الفاتورة
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Success Alert */}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            تم حذف الفاتورة بنجاح
          </Alert>
        )}

        {/* Warning Box */}
        <Paper
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: "#fff3e0",
            border: "2px solid #ff9800",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <WarningAmberIcon
            sx={{ fontSize: 40, color: "warning.main" }}
          />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.5 }}>
              تحذير!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              هذا الإجراء لا يمكن التراجع عنه. سيتم حذف الفاتورة نهائياً من النظام.
            </Typography>
          </Box>
        </Paper>

        <Divider sx={{ my: 2 }} />

        {/* Invoice Details */}
        <Paper
          sx={{
            p: 2,
            backgroundColor: "#f5f5f5",
            border: "1px solid #ddd",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, fontWeight: "bold", color: "error.main" }}
          >
            تفاصيل الفاتورة المراد حذفها:
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">
                <strong>رقم الفاتورة:</strong>
              </Typography>
              <Typography variant="body2">#{invoice.id}</Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">
                <strong>رقم الطلب:</strong>
              </Typography>
              <Typography variant="body2">#{invoice.order_id}</Typography>
            </Box>

            {invoice.user && (
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">
                  <strong>المستخدم:</strong>
                </Typography>
                <Typography variant="body2">{invoice.user.name}</Typography>
              </Box>
            )}

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">
                <strong>التاريخ:</strong>
              </Typography>
              <Typography variant="body2">{invoice.date}</Typography>
            </Box>

            <Divider sx={{ my: 0.5 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">
                <strong>الإجمالي الفرعي:</strong>
              </Typography>
              <Typography variant="body2">
                {parseFloat(invoice.sub_total).toFixed(2)}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="success.main">
                <strong>+ الخدمة ({invoice.service_ratio}%):</strong>
              </Typography>
              <Typography variant="body2" color="success.main">
                {parseFloat(invoice.service).toFixed(2)}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="success.main">
                <strong>+ التوصيل ({invoice.delivery_service_ratio}%):</strong>
              </Typography>
              <Typography variant="body2" color="success.main">
                {parseFloat(invoice.delivery_service).toFixed(2)}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="success.main">
                <strong>+ الضريبة ({invoice.tax_ratio}%):</strong>
              </Typography>
              <Typography variant="body2" color="success.main">
                {parseFloat(invoice.tax).toFixed(2)}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="error.main">
                <strong>- الخصم ({invoice.discount_ratio}%):</strong>
              </Typography>
              <Typography variant="body2" color="error.main">
                {parseFloat(invoice.discount).toFixed(2)}
              </Typography>
            </Box>

            <Divider sx={{ my: 0.5 }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                backgroundColor: "error.light",
                p: 1,
                borderRadius: 1,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                الإجمالي النهائي:
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: "error.dark" }}
              >
                {parseFloat(invoice.total_amount).toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Typography
          variant="body2"
          sx={{ mt: 2, textAlign: "center", color: "text.secondary" }}
        >
          هل أنت متأكد من حذف هذه الفاتورة؟
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          color="inherit"
        >
          إلغاء
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          disabled={loading}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
          startIcon={!loading && <DeleteIcon />}
        >
          {loading && <CircularProgress size={20} color="inherit" />}
          {loading ? "جاري الحذف..." : "تأكيد الحذف"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteInvoiceDialog;
